import { Document, Page } from "react-pdf";
import { useSelector } from "react-redux";

import ButtonLoader from "@/Components/Loaders/ButtonLoader";
import { formatCalendarDate, formatDateAtTime2 } from "@/Utils/helper";
import incidentIcon from "@/assets/images/incident.svg";
import UserChatBubble from "@/Components/Forms/FirmUserBtn";

export const LibraryUpdatedCard = ({
  doc,
  handleDocPreview,
  cardWidth = 255,
  rowIndex,
  cardPositionInRow,
}) => {
  const cardWidthPx = `${cardWidth}px`;
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const getCardBackground = () => {
    const isEvenRow = rowIndex % 2 === 0;
    const isEvenPosition = cardPositionInRow % 2 === 0;
    if (isEvenRow) return isEvenPosition ? "var(--primary-4)" : "var(--primary-2)";
    return isEvenPosition ? "var(--primary-2)" : "var(--primary-4)";
  };

  const incidentDate = formatCalendarDate(doc?.source_incident_date) || "—";

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ marginRight: "0px" }}
    >
      <div
        className="font-weight-semibold cursor-pointer updated-empty-card p-0"
        id="no-vertical-border"
        onClick={() => handleDocPreview(doc)}
        style={{
          width: cardWidthPx,
          minWidth: cardWidthPx,
          maxWidth: cardWidthPx,
          height: "369px",
          minHeight: "369px",
          border: "none",
          gap: "5px",
          background: getCardBackground(),
        }}
        key={doc.id}
      >
        <div>
          <div
            style={{ display: "flex", alignItems: "center", height: "21px" }}
            className="p-l-5 p-r-5"
          >
            <span
              className="d-flex align-items-center justify-content-center"
              style={{ display: "inline-block", marginRight: "5px", width: "15px", height: "15px" }}
            >
              <i
                className="ic ic-15 ic-file-colored cursor-pointer"
                style={{ width: "15px", height: "15px" }}
              />
            </span>
            <p title={doc?.document_name} className="name new-view-title">
              {doc?.document_name}
            </p>
          </div>
        </div>

        <div
          style={{
            height: "254px",
            maxHeight: "254px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {doc?.upload_url ? (
            <Document file={doc.upload_url} error={<BrokenPreviewImage />}>
              <Page
                width={cardWidth - 10}
                height={254}
                pageNumber={1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          ) : (
            <BrokenPreviewImage />
          )}
        </div>

        <div>
          <div className="updated-empty-card-sub-head first-sub-head mt-0 p-l-5 p-r-5">
            <div className="d-flex align-items-center" style={{ gap: "5px", overflow: "hidden" }}>
              <p style={{ color: "var(--primary-25)", fontWeight: "600" }}>Client:</p>
              <p
                title={doc?.source_client_name}
                className="text-truncate"
                style={{ fontWeight: "600" }}
              >
                {doc?.source_client_name || ""}
              </p>
            </div>
          </div>

          <div className="updated-empty-card-sub-head p-l-5 p-r-5">
            <p
              className="text-truncate d-flex align-items-center"
              style={{ fontWeight: "600", gap: "5px" }}
            >
              {doc?.case_type?.casetype_icon && (
                <span className="d-flex ic-15 align-items-center justify-content-center">
                  <img
                    src={doc?.case_type?.casetype_icon}
                    className="w-100 d-flex align-items-center justify-content-center"
                  />
                </span>
              )}
              {doc?.case_type?.name || ""}
            </p>
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <span className="d-flex ic-15 align-items-center justify-content-center">
                <img
                  src={incidentIcon}
                  className="w-100 d-flex align-items-center justify-content-center"
                />
              </span>

              <p style={{ fontWeight: "600" }}>{incidentDate}</p>
            </div>
          </div>

          <div className="updated-empty-card-sub-head p-l-5 p-r-5">
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <p style={{ color: "var(--primary-25)", fontWeight: "600" }}>Uploaded:</p>
              <p style={{ fontWeight: "600" }}>{formatDateAtTime2(doc?.created_at)}</p>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-center p-l-5 p-r-5">
            <UserChatBubble
              userId={doc?.created_by?.id}
              first_name={doc?.created_by?.first_name ?? ""}
              last_name={doc?.created_by?.last_name ?? ""}
              profilePic={doc?.created_by?.profile_pic ?? ""}
              is_active={doc?.created_by?.is_active}
              width={19}
              isActive={userOnlineStatus?.[doc?.created_by?.id]?.isOnline ?? false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const LibraryUpdatedEmptyCard = ({
  slot,
  page,
  item,
  isDragging,
  refetchLoading,
  handleDrop,
  handleUploadPopup,
  isSlotUploading,
  style,
}) => {
  return (
    <div
      onDrop={(e) => handleDrop(e, slot.id, page?.page?.id, item?.id)}
      className={`dropzone-${slot.id}-${item?.id}-${page.page.id} font-weight-semibold cursor-pointer updated-empty-card`}
      id="no-vertical-border"
      onClick={() => handleUploadPopup(slot?.id, item?.id, page?.page?.id)}
      style={{ ...style }}
    >
      {isSlotUploading(slot?.id, item?.id) && (isDragging || refetchLoading) ? (
        <div className="d-flex align-items-center justify-content-center">
          <ButtonLoader />
          <span style={{ marginLeft: "5px" }}>Uploading</span>
        </div>
      ) : (
        <div
          style={{ height: "100%" }}
          className="d-flex align-items-center justify-content-center"
        >
          <p
            className="name"
            style={{
              fontWeight: 600,
              paddingBottom: "14px",
              color: "var(--primary-25)",
              textAlign: "center",
            }}
          >
            {slot?.slot_name ? `Add ${slot?.slot_name} +` : "Add File +"}
          </p>
        </div>
      )}
    </div>
  );
};

const BrokenPreviewImage = () => {
  return (
    <svg height={24} width={24} viewBox="0 0 64 64" fill="#000000">
      <polygon
        fill="none"
        stroke="#000000"
        strokeWidth="2"
        strokeMiterlimit="10"
        points="23,1 55,1 55,63 9,63 9,15 "
      />
      <polyline
        fill="none"
        stroke="#000000"
        strokeWidth="2"
        strokeMiterlimit="10"
        points="9,15 23,15 23,1 "
      />
      <circle
        fill="none"
        stroke="#000000"
        strokeWidth="2"
        strokeMiterlimit="10"
        cx="32"
        cy="36"
        r="9"
      />
      <line
        fill="none"
        stroke="#000000"
        strokeWidth="2"
        strokeMiterlimit="10"
        x1="25.834"
        y1="29.834"
        x2="38.166"
        y2="42.166"
      />
    </svg>
  );
};
